// Portfolio Administration v1.0 PR 4a — shared "required X" matrix card.
// Used by the Insurance Required-Policy matrix (§7.4) and the Vendors
// Required-Vendor matrix (§7.7). One card per required slot; renders
// code + title + required-condition hint + indicators + status pill.

import { Check, X, AlertTriangle } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import type { ColorBand } from "./colorBand";

export interface RequirementIndicator {
  label: string;
  status: "ok" | "missing" | "warn";
}

export type RequirementStatusPill =
  | { kind: "ok"; label: string }
  | { kind: "warn"; label: string }
  | { kind: "missing"; label: string };

interface RequirementMatrixCardProps {
  code: string;
  title: string;
  /** Required-condition hint (e.g. "All loans", "Phoenix + Seller as loss payee"). */
  conditionHint?: string;
  /** Up to 2-3 boolean indicators (Coverage Verified, Renewal Tracked, …). */
  indicators?: RequirementIndicator[];
  /** Overall status pill (Current / Expires in N days / Missing). */
  statusPill: RequirementStatusPill;
  /** Outline color band per Convention 8. */
  band: ColorBand;
  onClick?: () => void;
  testId?: string;
}

const CARD_BORDER: Record<ColorBand, string> = {
  green: "border-status-success",
  yellow: "border-status-warning",
  orange: "border-status-warning",
  red: "border-status-error",
  neutral: "border-border-light",
};

const PILL_CLASS: Record<RequirementStatusPill["kind"], string> = {
  ok: "bg-status-success text-white-pure",
  warn: "bg-status-warning text-white-pure",
  missing: "bg-status-error text-white-pure",
};

function IndicatorRow({ ind }: { ind: RequirementIndicator }) {
  const icon =
    ind.status === "ok" ? (
      <Check size={14} className="text-status-success" aria-hidden />
    ) : ind.status === "warn" ? (
      <AlertTriangle size={14} className="text-status-warning" aria-hidden />
    ) : (
      <X size={14} className="text-status-error" aria-hidden />
    );
  return (
    <li className="flex items-center gap-space-2 text-caption text-onyx">
      {icon}
      <span>{ind.label}</span>
    </li>
  );
}

export function RequirementMatrixCard({
  code,
  title,
  conditionHint,
  indicators,
  statusPill,
  band,
  onClick,
  testId,
}: RequirementMatrixCardProps) {
  const className = cn(
    "flex flex-col gap-space-2 p-space-3 rounded-md bg-white-pure border-2 text-left w-full",
    CARD_BORDER[band],
    onClick && "hover:bg-white-warm transition-colors cursor-pointer",
  );
  const body = (
    <>
      <div className="flex items-start justify-between gap-space-2">
        <div className="min-w-0">
          <div className="font-display font-bold text-label uppercase tracking-label text-gold-shadow">
            {code}
          </div>
          <div className="font-display font-semibold text-small text-onyx mt-[2px]">
            {title}
          </div>
        </div>
        <span
          className={cn(
            "inline-flex items-center px-space-2 py-[2px] rounded-sm text-[10px] font-display font-bold uppercase tracking-[0.16em] shrink-0",
            PILL_CLASS[statusPill.kind],
          )}
        >
          {statusPill.label}
        </span>
      </div>
      {conditionHint && (
        <p className="text-caption text-gray-mid m-0">{conditionHint}</p>
      )}
      {indicators && indicators.length > 0 && (
        <ul className="flex flex-col gap-[2px] m-0 list-none p-0">
          {indicators.map((ind, idx) => (
            <IndicatorRow key={idx} ind={ind} />
          ))}
        </ul>
      )}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} data-testid={testId} className={className}>
      {body}
    </button>
  ) : (
    <div data-testid={testId} className={className}>
      {body}
    </div>
  );
}
